import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { AppointmentForm } from "./AppointmentForm";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

interface NavigationMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NavigationMenuImproved = ({ isOpen, onClose }: NavigationMenuProps) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const services = [
    {
      category: "Inbound Voice Agents",
      description: "AI-powered customer service solutions",
      items: [
        { title: "Customer Service", path: "/services/inbound-voice-agents/customer-service" },
        { title: "Healthcare Providers", path: "/services/inbound-voice-agents/healthcare-providers" },
        { title: "Insurance Companies", path: "/services/inbound-voice-agents/insurance-companies" },
        { title: "Realtor Office", path: "/services/inbound-voice-agents/realtor-office" },
        { title: "Restaurant Chains", path: "/services/inbound-voice-agents/restaurant-chains" },
        { title: "SaaS Companies", path: "/services/inbound-voice-agents/saas-companies" }
      ]
    },
    {
      category: "Outbound Voice Agents",
      description: "Automated outreach and communication",
      items: [
        { title: "Sales Outreach", path: "/services/outbound-voice-agents/sales-outreach" },
        { title: "Market Research", path: "/services/outbound-voice-agents/market-research" },
        { title: "Appointment Reminders", path: "/services/outbound-voice-agents/appointment-reminders" },
        { title: "Debt Collection", path: "/services/outbound-voice-agents/debt-collection" },
        { title: "Marketing Agency", path: "/services/outbound-voice-agents/marketing-agency" },
        { title: "Customer Retention", path: "/services/outbound-voice-agents/customer-retention" }
      ]
    },
    {
      category: "AI Copilots",
      description: "Intelligent assistance for your workforce",
      items: [
        { title: "Agent Assistance", path: "/services/ai-copilots/agent-assistance" },
        { title: "Document Processing", path: "/services/ai-copilots/document-processing" },
        { title: "Meeting Transcription", path: "/services/ai-copilots/meeting-transcription" },
        { title: "Email Drafting", path: "/services/ai-copilots/email-drafting" },
        { title: "Scheduling Optimization", path: "/services/ai-copilots/scheduling-optimization" },
        { title: "Research Assistance", path: "/services/ai-copilots/research-assistance" }
      ]
    },
    {
      category: "Automation",
      description: "End-to-end process automation",
      items: [
        { title: "Document Workflow", path: "/services/automation/document-workflow" },
        { title: "Employee Onboarding", path: "/services/automation/employee-onboarding" },
        { title: "Inventory Management", path: "/services/automation/inventory-management" },
        { title: "Customer Communications", path: "/services/automation/customer-communications" },
        { title: "Process Orchestration", path: "/services/automation/process-orchestration" },
        { title: "Data Integration", path: "/services/automation/data-integration" }
      ]
    }
  ];

  const toggleCategory = (categoryName: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryName)) {
      newExpanded.delete(categoryName);
    } else {
      newExpanded.add(categoryName);
    }
    setExpandedCategories(newExpanded);
  };

  return (
    <div className={`fixed inset-0 z-50 bg-black/50 transition-opacity ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
      <div className={`fixed top-0 right-0 h-full w-96 bg-white shadow-xl transform transition-transform ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="p-6 h-full flex flex-col">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl font-bold text-foreground">Menu</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-2"
              aria-label="Close menu"
            >
              ✕
            </button>
          </div>
          
          {/* Main Navigation Links */}
          <nav className="mb-6 space-y-2">
            <Link 
              to="/" 
              onClick={onClose}
              className="block text-foreground hover:text-primary transition-colors text-lg font-medium py-2"
            >
              Home
            </Link>
            <Link 
              to="/blog" 
              onClick={onClose}
              className="block text-foreground hover:text-primary transition-colors text-lg font-medium py-2"
            >
              Blog
            </Link>
            <Link 
              to="/contact" 
              onClick={onClose}
              className="block text-foreground hover:text-primary transition-colors text-lg font-medium py-2"
            >
              Contact
            </Link>
          </nav>
          
          <h3 className="text-lg font-bold text-foreground mb-4">Our Services</h3>
          
          <nav className="space-y-4 flex-1 overflow-y-auto">
            {services.map((category, index) => {
              const isExpanded = expandedCategories.has(category.category);
              
              return (
                <div key={index} className="border-b border-gray-100 pb-4 last:border-b-0">
                  <button
                    onClick={() => toggleCategory(category.category)}
                    className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                  >
                    <div className="text-left">
                      <h3 className="font-semibold text-foreground text-sm uppercase tracking-wide group-hover:text-primary transition-colors">
                        {category.category}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        {category.description}
                      </p>
                    </div>
                    <div className="text-gray-400 group-hover:text-primary transition-colors">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div className="mt-2 ml-4 space-y-1 animate-in slide-in-from-top-2 duration-200">
                      {category.items.map((item, itemIndex) => (
                        <Link
                          key={itemIndex}
                          to={item.path}
                          onClick={onClose}
                          className="block text-muted-foreground hover:text-primary transition-colors text-sm py-2 px-3 rounded hover:bg-gray-50"
                        >
                          {item.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
          
          <div className="mt-8 pt-6 border-t">
            <Dialog>
              <DialogTrigger asChild>
                <Button className="w-full">
                  Get Free Consultation
                </Button>
              </DialogTrigger>
              <AppointmentForm />
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
};